import _isEmpty from 'lodash/isEmpty';
import useWhiteLabelClient from './useWhiteLabelClient';
import utils from '../helpers/utils';
import {stringBundle} from '../../src/stringBundle';

/**
 * Interface for Adobe Target test configuration
 * Used for A/B testing different offers via Adobe Target
 */
interface AdobeTestConfig {
  /** Whether the A/B test is active */
  enabled?: boolean;
  /** Offers to show for users without credit features */
  non_credit_user_offers?: FinancialOffer[];
  /** Offers to show for users with credit features */
  offers_data?: FinancialOffer[];
  credit_insights_offers_data?: CreditInsightsOffer;
}

/**
 * Interface for Adobe Credit Freeze Unlocked Test configuration
 * Used for displaying special content when a user's credit file is unlocked
 */
interface AdobeCreditFreezeUnlockedTest {
  /** Whether the credit freeze unlock test is active */
  enabled?: boolean;
  /** Content to display in the unlock modal */
  unlock_modal_content?: {
    unlocked_title: string;
    unlocked_description: string;
    top_highlight: string;
    recommended_title: string;
    recommended_description: string;
    explore_button_text: string;
    explore_button_url: string;
  };
}

/**
 * Interface for the MoneyLion credit card offers adobe test
 */
interface AdobeMLCardOffersTest {
  enabled?: boolean;
}

/**
 * Interface for a single financial offer
 * Contains all the data needed to display an offer card including
 * text keys for localization, URLs, and vendor information
 */
interface FinancialOffer {
  /** Unique identifier for the offer */
  id: string;
  /** Key for the illustration to display */
  illustrationKey: string;
  /** Key for the header text in half-width view (empty string if not used) */
  halfHeaderTextKey: string;
  /** Key for the header text in full-width view */
  fullHeaderTextKey: string;
  /** Key for the body text in half-width view */
  halfBodyTextKey: string;
  /** Key for the body text in full-width view */
  fullBodyTextKey: string;
  /** Whether this offer should show a credit ratings modal */
  hasCreditRatingsModal: boolean;
  /** Key for the CTA button text in half-width view */
  halfCtaTextKey: string;
  /** Key for the CTA button text in full-width view */
  fullCtaTextKey: string;
  /** Environment variable key for the CTA URL */
  ctaURLKey: string;
  /** Direct URL for the CTA (takes precedence over ctaURLKey) */
  ctaURL: string;
  /** Vendor identifier for analytics */
  ctaVendor: string;
  /** Label used for Google Analytics tracking */
  gaLabel: string;
  /** Localized value for the header text in half-width view */
  halfHeaderTextValue: string;
  /** Localized value for the header text in full-width view */
  fullHeaderTextValue: string;
  /** Localized value for the body text in half-width view */
  halfBodyTextValue: string;
  /** Localized value for the body text in full-width view */
  fullBodyTextValue: string;
  /** Localized value for the CTA button text in half-width view */
  halfCtaTextValue: string;
  /** Localized value for the CTA button text in full-width view */
  fullCtaTextValue: string;
}

/**
 * Represents a financial offer with detailed information for credit insights.
 */
interface CreditInsightsOffer {
  /** Unique identifier for the credit insights offer */
  id: string;
  /** Key for the body text used for localization */
  bodyTextKey: string;
  /** Key for the call-to-action (CTA) text used for localization */
  ctaKey: string;
  /** Environment variable key for the CTA URL */
  ctaURLKey: string;
  /** Direct URL for the CTA (takes precedence over ctaURLKey) */
  ctaURL: string;
  /** Vendor identifier for analytics */
  ctaVendor: string;
  /** Label used for Google Analytics tracking */
  gaLabel: string;
  /** Localized value for the body text */
  bodyTextValue: string;
  /** Localized value for the CTA text */
  ctaTextValue: string;
}
interface FinancialOffersHookResult {
  /** Whether credit offers should be shown to the user */
  isCreditOffersActive: boolean;
  /** Array of financial offers to display */
  creditOffersData: FinancialOffer[];
  /** Credit insights offer data */
  creditInsightsOffersData: CreditInsightsOffer;
  /** Whether the credit freeze unlock feature is active */
  isCreditFreezeUnlockActive: boolean;
  /** Content to display in the credit freeze unlock modal */
  creditFreezeUnlockContent?: {
    unlocked_title: string;
    unlocked_description: string;
    top_highlight: string;
    recommended_title: string;
    recommended_description: string;
    explore_button_text: string;
    explore_button_url: string;
  };
  /** Whether MoneyLion card offers should be shown */
  isMLCardOffersActive: boolean;
}

/**
 * Creates a string resolver function that retrieves localized strings based on a provided key.
 *
 * The resolver uses a localized string bundle if provided, or attempts to fetch a default string
 * bundle (e.g., for the English locale) if no bundle is supplied. If an error occurs during the
 * retrieval of the string bundle, it gracefully falls back to an empty object.
 *
 * @param localizedStringBundle - An optional record of key-value pairs representing localized strings.
 *                                If not provided, the function attempts to fetch a default string bundle.
 *
 * @returns A function that takes a string key and returns the corresponding localized string.
 *          If the key is not found in the string bundle, the key itself is returned as a fallback.
 */
const createStringResolver = (localizedStringBundle: Record<string, string> = {}) => {
  let effectiveStringBundle: Record<string, string> = {};

  try {
    // If localizedStringBundle is provided, use it directly
    // Otherwise try to get localized strings from the stringBundle function
    if (Object.keys(localizedStringBundle).length > 0) {
      effectiveStringBundle = localizedStringBundle;
    } else {
      // Try to get the string bundle, but handle errors gracefully
      try {
        effectiveStringBundle = stringBundle('en') || {};
      } catch {
        effectiveStringBundle = {};
      }
    }
  } catch (error) {
    // In case of error (like in test environment), use empty object
    console.warn('Error loading string bundle:', error);
    effectiveStringBundle = {};
  }

  return (key: string): string => effectiveStringBundle[key] || key;
};

/**
 * Retrieves a list of financial offers with localized text values and metadata.
 *
 * This function generates an array of financial offer objects, each containing
 * information such as headers, body text, call-to-action (CTA) details, and URLs.
 * The text values are resolved using a provided localized string bundle.
 *
 * @param localizedStringBundle - A record of key-value pairs where keys are string identifiers
 * and values are localized strings. Defaults to an empty object if not provided.
 *
 * @returns An array of `FinancialOffer` objects, each representing a financial offer
 * with localized text values and associated metadata.
 */
const getCreditOffersData = (localizedStringBundle: Record<string, string> = {}): FinancialOffer[] => {
  const getLocalizedString = createStringResolver(localizedStringBundle);

  return [
    {
      id: 'virtual-credit-cards',
      illustrationKey: 'credit_cards',
      halfHeaderTextKey: '',
      fullHeaderTextKey: 'FINANCIAL_OFFERS_VIRTUAL_CREDIT_CARD_HEADER',
      halfBodyTextKey: 'FINANCIAL_OFFERS_VIRTUAL_CREDIT_CARD_MINI_TAGLINE',
      fullBodyTextKey: 'FINANCIAL_OFFERS_VIRTUAL_CREDIT_CARD_TAGLINE',
      hasCreditRatingsModal: false,
      halfCtaTextKey: 'EXPLORE',
      fullCtaTextKey: 'FINANCIAL_OFFERS_EXPLORE_CREDIT_CARDS',
      ctaURLKey: 'REACT_APP_SAFER_ONLINE_TRANSACTIONS',
      ctaURL: window.REACT_APP_SAFER_ONLINE_TRANSACTIONS || '',
      ctaVendor: 'cardratings',
      gaLabel: 'Security Credit Card',

      halfHeaderTextValue: '',
      fullHeaderTextValue: getLocalizedString('FINANCIAL_OFFERS_VIRTUAL_CREDIT_CARD_HEADER'),
      halfBodyTextValue: getLocalizedString('FINANCIAL_OFFERS_VIRTUAL_CREDIT_CARD_MINI_TAGLINE'),
      fullBodyTextValue: getLocalizedString('FINANCIAL_OFFERS_VIRTUAL_CREDIT_CARD_TAGLINE'),
      halfCtaTextValue: getLocalizedString('EXPLORE'),
      fullCtaTextValue: getLocalizedString('FINANCIAL_OFFERS_EXPLORE_CREDIT_CARDS'),
    },
    {
      id: 'high-interest-savings',
      illustrationKey: 'bank',
      halfHeaderTextKey: '',
      fullHeaderTextKey: 'FINANCIAL_OFFERS_HIGH_INTEREST_SAVINGS_HEADER',
      halfBodyTextKey: 'FINANCIAL_OFFERS_HIGH_INTEREST_SAVINGS_MINI_TAGLINE',
      fullBodyTextKey: 'FINANCIAL_OFFERS_HIGH_INTEREST_SAVINGS_TAGLINE',
      hasCreditRatingsModal: false,
      halfCtaTextKey: 'EXPLORE',
      fullCtaTextKey: 'FINANCIAL_OFFERS_EXPLORE_SAVINGS',
      ctaURLKey: 'REACT_APP_HIGH_YIELD_SAVING_ACCOUNT_VENDOR_URL',
      ctaURL: window.REACT_APP_HIGH_YIELD_SAVING_ACCOUNT_VENDOR_URL || '',
      ctaVendor: 'mybanktracker',
      gaLabel: 'Saving Accounts',

      halfHeaderTextValue: '',
      fullHeaderTextValue: getLocalizedString('FINANCIAL_OFFERS_HIGH_INTEREST_SAVINGS_HEADER'),
      halfBodyTextValue: getLocalizedString('FINANCIAL_OFFERS_HIGH_INTEREST_SAVINGS_MINI_TAGLINE'),
      fullBodyTextValue: getLocalizedString('FINANCIAL_OFFERS_HIGH_INTEREST_SAVINGS_TAGLINE'),
      halfCtaTextValue: getLocalizedString('EXPLORE'),
      fullCtaTextValue: getLocalizedString('FINANCIAL_OFFERS_EXPLORE_SAVINGS'),
    },
    {
      id: 'travel-benefits',
      illustrationKey: 'plane',
      halfHeaderTextKey: '',
      fullHeaderTextKey: 'FINANCIAL_OFFERS_TRAVEL_BENEFITS_HEADER',
      halfBodyTextKey: 'FINANCIAL_OFFERS_TRAVEL_BENEFITS_MINI_TAGLINE',
      fullBodyTextKey: 'FINANCIAL_OFFERS_TRAVEL_BENEFITS_TAGLINE',
      hasCreditRatingsModal: true,
      halfCtaTextKey: 'EXPLORE',
      fullCtaTextKey: 'FINANCIAL_OFFERS_EXPLORE_TRAVEL_BENEFITS',
      ctaURLKey: 'REACT_APP_CREDIT_TRAVEL_BENEFITS_VENDOR_URL',
      ctaURL: window.REACT_APP_CREDIT_TRAVEL_BENEFITS_VENDOR_URL || '',
      ctaVendor: 'cardratings',
      gaLabel: 'Travel Credit Card',

      halfHeaderTextValue: '',
      fullHeaderTextValue: getLocalizedString('FINANCIAL_OFFERS_TRAVEL_BENEFITS_HEADER'),
      halfBodyTextValue: getLocalizedString('FINANCIAL_OFFERS_TRAVEL_BENEFITS_MINI_TAGLINE'),
      fullBodyTextValue: getLocalizedString('FINANCIAL_OFFERS_TRAVEL_BENEFITS_TAGLINE'),
      halfCtaTextValue: getLocalizedString('EXPLORE'),
      fullCtaTextValue: getLocalizedString('FINANCIAL_OFFERS_EXPLORE_TRAVEL_BENEFITS'),
    },
  ];
};

/**
 * Generates a Credit Insights Offer object with localized strings and configuration.
 *
 * @param localizedStringBundle - A record containing key-value pairs for localized strings.
 * @returns An object of type `CreditInsightsOffer` containing details about the virtual credit card offer,
 *          including localized text values, URLs, and metadata for analytics.
 */
const getCreditInsightsOffersData = (localizedStringBundle: Record<string, string> = {}): CreditInsightsOffer => {
  const getLocalizedString = createStringResolver(localizedStringBundle);

  const bodyTextKey = 'FINANCIAL_OFFERS_VIRTUAL_CREDIT_CARD_TAGLINE_2';
  const ctaURLKey = 'REACT_APP_SAFER_ONLINE_TRANSACTIONS';
  const ctaKey = 'EXPLORE';

  return {
    id: 'virtual-credit-cards',
    bodyTextKey,
    ctaKey,
    ctaURLKey,
    ctaURL: window.REACT_APP_SAFER_ONLINE_TRANSACTIONS || '',
    ctaVendor: 'cardratings',
    gaLabel: 'Security Credit Card',
    bodyTextValue: getLocalizedString(bodyTextKey),
    ctaTextValue: getLocalizedString(ctaKey),
  };
};

/**
 * Custom hook for managing financial offers functionality
 *
 * This hook determines whether financial offers should be displayed to the user and
 * which offers should be shown based on various criteria:
 *
 * 1. User's product features and permissions
 * 2. Adobe Target A/B test configuration
 * 3. White label client status
 * 4. User's credit status
 *
 * The hook handles localization of offer content and supports both default offers
 * and dynamically configured offers from Adobe Target.
 *
 * @param {ProductFeatures} productFeatures - User's product features and permissions
 * @param {Record<string, string>} localizedStringBundle - Required string bundle for localization
 *
 * @returns {Object} Financial offers configuration
 * @returns {boolean} .isCreditOffersActive - Whether financial offers should be shown
 * @returns {boolean} isMLCardOffersActive - Whether MoneyLion card offers should be shown
 * @returns {FinancialOffer[]} .creditOffersData - Array of offers to display
 *
 * @example
 * // Basic usage
 * const { isCreditOffersActive, creditOffersData } = useFinancialOffers(userFeatures, stringBundle);
 * if (isCreditOffersActive) {
 *   // Render offers using creditOffersData
 * }
 *
 * @example
 * // With custom string bundle
 * const customStrings = { 'EXPLORE': 'Discover Now' };
 * const { creditOffersData } = useFinancialOffers(userFeatures, customStrings);
 */
export const useFinancialOffers = (
  productFeatures: ProductFeatures,
  localizedStringBundle: Record<string, string>
): FinancialOffersHookResult => {
  const defaultCreditOffersData = getCreditOffersData(localizedStringBundle);
  const defaultCreditInsightsOffersData = getCreditInsightsOffersData(localizedStringBundle);

  const {adobeCreditOffersTest = {}} = window as Window & {
    adobeCreditOffersTest?: AdobeTestConfig;
  };

  const {adobeCreditFreezeUnlockedTest = {}} = window as Window & {
    adobeCreditFreezeUnlockedTest?: AdobeCreditFreezeUnlockedTest;
  };

  const {adobeMLCardOffersTest = {}} = window as Window & {
    adobeMLCardOffersTest?: AdobeMLCardOffersTest;
  };

  const isCreditOffersTestActive = (!_isEmpty(adobeCreditOffersTest) && adobeCreditOffersTest.enabled) || false;
  const isCreditFreezeUnlockActive =
    (!_isEmpty(adobeCreditFreezeUnlockedTest) && adobeCreditFreezeUnlockedTest.enabled) || false;
  const isMLCardOffersActive = (!_isEmpty(adobeMLCardOffersTest) && adobeMLCardOffersTest.enabled) || false;

  const whiteLabelClient: string = useWhiteLabelClient();

  const hasFinancialOffers = utils.hasFinancialOffers(productFeatures);
  const isNonCreditUser = !utils.isCreditUser(productFeatures);

  const creditOffersData = isNonCreditUser
    ? (!_isEmpty(adobeCreditOffersTest) && adobeCreditOffersTest.non_credit_user_offers) || defaultCreditOffersData
    : (!_isEmpty(adobeCreditOffersTest) && adobeCreditOffersTest.offers_data) || defaultCreditOffersData;

  // Old credit offers will only be available if new ML credit offers is not enabled and the rest of the conditions are met

  return {
    isCreditOffersActive: !isMLCardOffersActive && hasFinancialOffers && isCreditOffersTestActive && !whiteLabelClient,
    creditOffersData,
    creditInsightsOffersData:
      (!_isEmpty(adobeCreditOffersTest) && adobeCreditOffersTest.credit_insights_offers_data) ||
      defaultCreditInsightsOffersData,
    isCreditFreezeUnlockActive,
    creditFreezeUnlockContent: adobeCreditFreezeUnlockedTest.unlock_modal_content,
    isMLCardOffersActive: hasFinancialOffers && isMLCardOffersActive && !whiteLabelClient,
  };
};
